import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, ShoppingCartIcon, MapPinIcon, ChevronDownIcon, HeartIcon, TrashIcon, XIcon, ChevronRightIcon, TrendingUpIcon, ShoppingBagIcon, TagIcon, HeartPulseIcon, ActivityIcon, BrainIcon, ThermometerIcon, PillIcon, StethoscopeIcon, GlobeIcon, UserIcon, PackageIcon, LogOutIcon, ClockIcon, MenuIcon, ScanBarcodeIcon, BellIcon } from 'lucide-react';
import { LoyaltyCard } from './account/LoyaltyCard';
import { MobileExperienceSwitcher } from './home/MobileExperienceSwitcher';
import { LoyaltyOnboardingBottomSheet } from './modals/LoyaltyOnboardingBottomSheet';
import { AuthBottomSheet } from './modals/AuthBottomSheet';
import { LanguageBottomSheet } from './modals/LanguageBottomSheet';
import { useStore } from '../lib/store';
import { POPULAR_SEARCHES, TRENDING_SEARCHES, TRENDING_PRODUCTS } from '../data/searchData';
import { nowQuickCategories } from '../data/nowHome';
import { nowQuickLists } from '../data/nowPersonalization';
// Debounce utility
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}
interface MobileHomeHeaderProps {
  selectedLocation: {
    name: string;
  } | null;
  onOpenLocationModal: () => void;
  alwaysWhiteBackground?: boolean;
  transparentAtTop?: boolean;
  hidePrimaryActions?: boolean;
  showExperienceSwitcher?: boolean;
  activeExperience?: 'innova' | 'rx';
}
export function MobileHomeHeader({
  selectedLocation,
  onOpenLocationModal,
  alwaysWhiteBackground = false,
  transparentAtTop = false,
  hidePrimaryActions = false,
  showExperienceSwitcher = false,
  activeExperience
}: MobileHomeHeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    cart: cartItems,
    wishlist: wishlistItems,
    cartCount,
    cartTotal,
    language,
    setLanguage,
    searchQuery,
    setSearchQuery,
    recentSearches,
    addRecentSearch,
    clearRecentSearches
  } = useStore();
  const [isScrolled, setIsScrolled] = useState(false);
  const [showCartMenu, setShowCartMenu] = useState(false);
  const [showCategoriesMenu, setShowCategoriesMenu] = useState(false);
  const currentCartCount = cartCount();
  const currentWishlistCount = wishlistItems.length;
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchResultsLimit, setSearchResultsLimit] = useState(5);
  const cartMenuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  // Check if selected location is a pickup branch
  const isPickupLocation = selectedLocation && 'distance' in selectedLocation;
  // States for bottom sheets
  const [languageBottomSheetOpen, setLanguageBottomSheetOpen] = useState(false);
  const [authBottomSheetOpen, setAuthBottomSheetOpen] = useState(false);
  const [loyaltyBottomSheetOpen, setLoyaltyBottomSheetOpen] = useState(false);
  const [loyaltyProgress, setLoyaltyProgress] = useState(0);
  const [loyaltyData, setLoyaltyData] = useState<{
    memberId?: string;
    pointsBalance?: number;
    status?: 'active' | 'inactive';
    tier?: 'silver' | 'gold' | 'platinum';
    isEnrolled?: boolean;
  }>({});
  // Calculate loyalty progress from localStorage
  useEffect(() => {
    const savedData = localStorage.getItem('loyaltyOnboarding');
    if (savedData) {
      const data = JSON.parse(savedData);
      const requiredFields = [data.gender, data.maritalStatus, data.nationality, data.dateOfBirth, data.workingSector, data.city, data.district, data.preferredLanguage];
      const completedRequired = requiredFields.filter(field => field !== '').length;
      const progress = Math.round(completedRequired / requiredFields.length * 100);
      setLoyaltyProgress(progress);
      if (progress === 100) {
        setLoyaltyData({
          memberId: '1234567890',
          pointsBalance: 3250,
          status: 'active',
          tier: 'gold',
          isEnrolled: true
        });
      } else {
        setLoyaltyData({
          isEnrolled: false
        });
      }
    }
  }, [loyaltyBottomSheetOpen]);
  const openAuthModal = () => {
    setAuthBottomSheetOpen(true);
  };
  const handleLanguageChange = (lang: string) => {
    setLanguage(lang as 'en' | 'ar');
  };
  const handleAuthSuccess = (isNewUser: boolean) => {
    setAuthBottomSheetOpen(false);
    if (isNewUser && loyaltyProgress < 100) {
      setTimeout(() => setLoyaltyBottomSheetOpen(true), 500);
    }
  };
  const handleLoyaltyComplete = (data: any) => {
    localStorage.setItem('loyaltyOnboarding', JSON.stringify(data));
    setLoyaltyProgress(100);
    setLoyaltyBottomSheetOpen(false);
    setLoyaltyData({
      memberId: '1234567890',
      pointsBalance: 0,
      status: 'active',
      tier: 'silver',
      isEnrolled: true
    });
  };
  const handleSearchFocus = () => {
    setSearchFocused(true);
    setShowSearchResults(true);
  };
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, [setSearchQuery]);
  useEffect(() => {
    if (debouncedSearchQuery) {
      setShowSearchResults(true);
    }
  }, [debouncedSearchQuery]);
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      addRecentSearch(searchQuery.trim());
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setShowSearchResults(false);
      setSearchFocused(false);
    }
  };
  const handleSearchSelect = (query: string) => {
    setSearchQuery(query);
    navigate(`/search?q=${encodeURIComponent(query)}`);
    setShowSearchResults(false);
    setSearchFocused(false);
  };
  const handleProductClick = (productId: number) => {
    navigate(`/product/${productId}?from=now`);
    setShowSearchResults(false);
    setSearchFocused(false);
  };
  const handleNowCategorySearch = (slug: string) => {
    navigate(`/category/${slug}?from=now`);
    setShowSearchResults(false);
    setSearchFocused(false);
  };
  const currentCartTotal = cartTotal();
  const conditions = [{
    icon: <HeartPulseIcon className="w-4 h-4 text-red-600" data-id="element-2597" data-mp="gwrsuc07jsws" />,
    name: 'Heart Health',
    slug: 'heart-health'
  }, {
    icon: <ActivityIcon className="w-4 h-4 text-blue-600" data-id="element-2598" data-mp="cxj1fdsn474e" />,
    name: 'Diabetes',
    slug: 'diabetes'
  }, {
    icon: <BrainIcon className="w-4 h-4 text-purple-600" data-id="element-2599" data-mp="2b987o172kfd9" />,
    name: 'Mental Health',
    slug: 'mental-health'
  }, {
    icon: <ThermometerIcon className="w-4 h-4 text-orange-600" data-id="element-2600" data-mp="3p843zzx1hnpj" />,
    name: 'Pain Relief',
    slug: 'pain-relief'
  }, {
    icon: <PillIcon className="w-4 h-4 text-green-600" data-id="element-2601" data-mp="3u54845abwge0" />,
    name: 'Digestive Health',
    slug: 'digestive-health'
  }, {
    icon: <StethoscopeIcon className="w-4 h-4 text-cyan-600" data-id="element-2602" data-mp="6tle6govo03u" />,
    name: 'Respiratory',
    slug: 'respiratory'
  }];
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY >= 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cartMenuRef.current && !cartMenuRef.current.contains(event.target as Node)) {
        setShowCartMenu(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        // Only close if not focused on input
        if (!searchFocused && !searchQuery) {
          setShowSearchResults(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [searchFocused, searchQuery]);
  const isSearchActive = showSearchResults || searchFocused;
  useEffect(() => {
    if (typeof document !== 'undefined' && document.body) {
      document.body.style.overflow = isSearchActive ? 'hidden' : 'auto';
    }
    return () => {
      if (typeof document !== 'undefined' && document.body) {
        document.body.style.overflow = 'auto';
      }
    };
  }, [isSearchActive]);
  const handleCategoryClick = (slug: string) => {
    setShowCategoriesMenu(false);
    navigate(`/category/${slug}`);
  };
  const handleConditionClick = (slug: string) => {
    setShowCategoriesMenu(false);
    navigate(`/category/${slug}`);
  };
  return <>
      <div className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,box-shadow] duration-200 m-0 ${alwaysWhiteBackground ? 'bg-white border-b border-gray-100 shadow-[0_2px_6px_rgba(30,43,85,0.035)]' : isScrolled ? 'bg-white border-b border-gray-100 shadow-[0_2px_6px_rgba(30,43,85,0.035)]' : transparentAtTop ? isSearchActive ? 'bg-white border-b border-gray-100 shadow-[0_2px_6px_rgba(30,43,85,0.035)]' : 'bg-transparent' : 'bg-white border-b border-gray-100 shadow-[0_2px_6px_rgba(30,43,85,0.035)]'}`} style={{
      paddingTop: 'env(safe-area-inset-top)'
    }} data-id="element-2603" data-mp="30o8llufxv00s">
        {!isSearchActive && !hidePrimaryActions && <div className={`flex items-center gap-2 px-4 transition-[padding] duration-200 ${isScrolled ? 'py-1.5' : 'pb-1 pt-2'}`} data-id="element-2604" data-mp="1xvd4rig5x14n">
            <button type="button" onClick={onOpenLocationModal} className="flex h-11 min-w-0 flex-1 items-center gap-2 rounded-xl bg-white px-3 text-left text-[#1E2B55] shadow-[0_1px_3px_rgba(30,43,85,0.06)]" aria-label="Change delivery location" data-id="element-2605" data-mp="22v906tasnk8l">
              <MapPinIcon className="h-4 w-4 flex-shrink-0" strokeWidth={2} data-id="element-2606" data-mp="3qju7fhppnaqq" />
              <span className="min-w-0 flex-1 truncate text-sm" data-id="element-2607" data-mp="3qlx7yvohom9p">
                <span className="font-medium text-gray-600" data-id="element-2608" data-mp="3ctqpv41s057m">{isPickupLocation ? 'Pickup: ' : 'Deliver to: '}</span>
                <span className="font-bold text-gray-900" data-id="element-2609" data-mp="3gukbfqrqx7xc">{selectedLocation?.name || 'Select Location'}</span>
              </span>
              <ChevronDownIcon className="h-4 w-4 flex-shrink-0" strokeWidth={2} data-id="element-2610" data-mp="8ceex4b7vw5v" />
            </button>
            <button type="button" onClick={() => setSearchFocused(true)} className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-white text-[#1E2B55] shadow-[0_1px_3px_rgba(30,43,85,0.06)]" aria-label="Search" data-id="element-2611" data-mp="3vixdn5hffn0a">
              <SearchIcon className="h-5 w-5" strokeWidth={2} data-id="element-2612" data-mp="2p2lb6s7kniyw" />
            </button>
            <button type="button" onClick={() => navigate('/cart')} className="relative flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-white text-[#1E2B55] shadow-[0_1px_3px_rgba(30,43,85,0.06)]" aria-label={`Cart, ${currentCartCount} items`} data-id="element-2613" data-mp="2p0iczkw9mjj3">
              <ShoppingCartIcon className="h-5 w-5" strokeWidth={2} data-id="element-2614" data-mp="2lknw3hsvwtod" />
              {currentCartCount > 0 && <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white" data-id="element-2615" data-mp="1xweaogzl27xr">
                  {currentCartCount}
                </span>}
            </button>
          </div>}
        {isSearchActive && <div className={`flex items-center gap-2.5 px-4 transition-all duration-300 ${isSearchActive ? 'py-2' : alwaysWhiteBackground ? 'pb-2' : transparentAtTop ? showExperienceSwitcher ? 'pb-1' : 'pb-3' : 'py-3'}`} data-id="element-2616" data-mp="22sv3hohq9ah6">
          {isSearchActive && <button onClick={() => {
          setShowSearchResults(false);
          setSearchFocused(false);
        }} className="p-1 -ml-1 text-gray-500 hover:text-gray-700" data-id="element-2617" data-mp="3a6yryn5sbjz8">
              <ChevronRightIcon className="w-6 h-6 rotate-180" data-id="element-2618" data-mp="3jzxqoxuzwqwk" />
            </button>}
          <div className="flex-1 relative" ref={searchRef} data-id="element-2619" data-mp="3nyn4usnuych9">
            <form onSubmit={handleSearchSubmit} data-id="element-2620" data-mp="2ra7belhmniyv">
              <div className={`relative rounded-lg transition-all flex items-center bg-white border border-gray-200 shadow-sm`} data-id="element-2621" data-mp="2rbidmgvfcinv">
                <SearchIcon className="absolute left-3 text-gray-400 w-5 h-5 pointer-events-none z-10" data-id="element-2622" data-mp="3092is0weqz3r" />
                <input type="text" placeholder="Search Personal Care" value={searchQuery} onChange={e => handleSearchChange(e.target.value)} onFocus={handleSearchFocus} className="w-full pl-10 pr-10 py-2.5 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none bg-transparent" data-id="element-2623" data-mp="35ai31qqrh1l3" />
                {searchQuery ? <button type="button" onClick={() => {
                setSearchQuery('');
              }} className="absolute right-3 text-gray-400 hover:text-gray-600 z-10" data-id="element-2624" data-mp="kwl5p7wwskvd">
                    <XIcon className="w-5 h-5" data-id="element-2625" data-mp="btsfl7zgqf74" />
                  </button> : <button type="button" className="absolute right-3 text-[#1E2B55] hover:text-blue-700 z-10" data-id="element-2626" data-mp="6z4xpryysj1i">
                    <ScanBarcodeIcon className="w-5 h-5" strokeWidth={1.5} data-id="element-2627" data-mp="3u32z9hftmi4i" />
                  </button>}
              </div>
            </form>
          </div>
          {!isSearchActive && <>
              <button type="button" onClick={() => navigate('/menu')} aria-label="Open menu" className={`rounded-lg bg-white hover:bg-gray-50 transition-all flex-shrink-0 p-3 ${alwaysWhiteBackground || isScrolled ? 'border border-gray-300' : 'shadow-lg'}`} data-id="element-2628" data-mp="1s8ahlw7njda7">
                <MenuIcon className="text-[#1E2B55] w-6 h-6" strokeWidth={2} data-id="element-2629" data-mp="1udz0tmalisnr" />
              </button>
              <div className="relative flex-shrink-0" ref={cartMenuRef} data-id="element-2630" data-mp="1gmli2akihyts">
                <button onClick={() => setShowCartMenu(!showCartMenu)} className={`rounded-lg bg-white hover:bg-gray-50 transition-all relative flex-shrink-0 p-3 ${alwaysWhiteBackground || isScrolled ? 'border border-gray-300' : 'shadow-lg'}`} data-id="element-2631" data-mp="1glaiyo20hf90">
                  <ShoppingCartIcon className="text-[#1E2B55] w-6 h-6" strokeWidth={2} data-id="element-2632" data-mp="1blo6igu0c319" />
                  {currentCartCount > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-sm" data-id="element-2633" data-mp="12p5agvqhbz0g">
                      {currentCartCount}
                    </span>}
                </button>
                {showCartMenu && <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50" data-id="element-2634" data-mp="o0vj5c1asgzw">
                    <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-center" data-id="element-2635" data-mp="11zd3czrzvbd2">
                      <div data-id="element-2636" data-mp="x0ilq15rjsuj">
                        <p className="text-sm font-medium text-gray-900" data-id="element-2637" data-mp="wz7jw6vvov62">
                          Shopping Cart
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5" data-id="element-2638" data-mp="16qleq1cvx6sv">
                          {cartItems.reduce((total, item) => total + item.quantity, 0)}{' '}
                          items
                        </p>
                      </div>
                      <button onClick={() => {
                  setShowCartMenu(false);
                  navigate('/cart');
                }} className="text-xs font-medium text-[#1E2B55] hover:underline" data-id="element-2639" data-mp="dfyf3idgk4a6">
                        View Cart
                      </button>
                    </div>
                    {cartItems.length > 0 ? <>
                        <div className="py-2 max-h-80 overflow-y-auto" data-id="element-2640" data-mp="113g8ficdfaf9">
                          {cartItems.map(item => <div key={item.id} className="flex items-center px-4 py-3 hover:bg-gray-50" data-id="element-2641" data-mp="164dqzw1am6ha">
                              <div className="flex-shrink-0 w-16 h-16 bg-gray-100 rounded-md overflow-hidden" data-id="element-2642" data-mp="37vqucgnm46b8">
                                <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-2643" data-mp="3bwj378sko96z" />
                              </div>
                              <div className="ml-3 flex-1 min-w-0" data-id="element-2644" data-mp="2unqay9wcsgn2">
                                <p className="text-sm font-medium text-gray-900 truncate" data-id="element-2645" data-mp="2qld7js52wutg">
                                  {item.name}
                                </p>
                                <div className="flex items-center mt-1" data-id="element-2646" data-mp="3n8zzpdv4dr13">
                                  <p className="text-sm font-bold text-[#1E2B55]" data-id="element-2647" data-mp="3icsq9b3ilxee">
                                    SAR {item.price.toFixed(2)}
                                  </p>
                                  <span className="text-xs text-gray-500 mx-1" data-id="element-2648" data-mp="39at82fgrkn9v">
                                    ×
                                  </span>
                                  <p className="text-xs text-gray-500" data-id="element-2649" data-mp="1xwph7i5h3f91">
                                    {item.quantity}
                                  </p>
                                </div>
                              </div>
                              <div className="ml-2 flex-shrink-0" data-id="element-2650" data-mp="5vxsei1tcgrk">
                                <button className="p-1 rounded-full text-gray-400 hover:text-red-500" data-id="element-2651" data-mp="3o0rdcvtf1fu7">
                                  <TrashIcon className="w-4 h-4" data-id="element-2652" data-mp="3sz2m137z5kau" />
                                </button>
                              </div>
                            </div>)}
                        </div>
                        <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 rounded-b-lg" data-id="element-2653" data-mp="phkgx3jl6re4">
                          <div className="flex justify-between items-center mb-3" data-id="element-2654" data-mp="at6fmx1tjazo">
                            <span className="text-sm font-medium text-gray-600" data-id="element-2655" data-mp="fs0whtg86ph5">
                              Total
                            </span>
                            <span className="text-lg font-bold text-[#1E2B55]" data-id="element-2656" data-mp="13gttlt8l33dc">
                              {currentCartTotal.toFixed(2)} SAR
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2" data-id="element-2657" data-mp="2o0p83do7wgdy">
                            <button onClick={() => {
                      setShowCartMenu(false);
                      navigate('/cart');
                    }} className="text-center py-2 px-4 border border-[#1E2B55] text-[#1E2B55] font-medium rounded-md hover:bg-blue-50 transition-colors text-sm" data-id="element-2658" data-mp="2o0fihr734b8s">
                              View Cart
                            </button>
                            <button onClick={() => {
                      setShowCartMenu(false);
                      navigate('/checkout');
                    }} className="text-center py-2 px-4 bg-[#1E2B55] text-white font-medium rounded-md hover:bg-[#162041] transition-colors text-sm" data-id="element-2659" data-mp="2j2w3uq6h5wgb">
                              Checkout
                            </button>
                          </div>
                        </div>
                      </> : <div className="py-8 text-center" data-id="element-2660" data-mp="3p7ry0o4cqmvl">
                        <ShoppingCartIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-2661" data-mp="3k748k6vrue3h" />
                        <p className="text-gray-500" data-id="element-2662" data-mp="21918shpd63t">Your cart is empty</p>
                        <button onClick={() => {
                  setShowCartMenu(false);
                  navigate('/');
                }} className="mt-3 inline-block text-sm font-medium text-[#1E2B55] hover:underline" data-id="element-2663" data-mp="3t822fwchx58c">
                          Continue Shopping
                        </button>
                      </div>}
                  </div>}
              </div>
            </>}
          </div>}
        {showExperienceSwitcher && !isSearchActive && <MobileExperienceSwitcher isCompact={isScrolled} activeExperience={activeExperience ?? (location.pathname.startsWith('/rx') ? 'rx' : 'innova')} onSelect={experience => {
        if (experience === 'rx') navigate('/rx');
        if (experience === 'innova') navigate('/');
      }} data-id="element-2664" data-mp="14ek78h6b8vi3" />}
        <AnimatePresence data-id="element-2665" data-mp="gmmwktelk9ik">
          {showSearchResults && (searchFocused || searchQuery) && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-x-0 top-[56px] bottom-0 bg-white z-50 overflow-y-auto pb-20" style={{
          top: 'calc(56px + env(safe-area-inset-top))'
        }} data-id="element-2666" data-mp="lh96st8xzw12">
              <div className="p-4 space-y-6" data-id="element-2667" data-mp="24e44y6jebub4">
                {!searchQuery && recentSearches.length > 0 && <div data-id="element-2668" data-mp="1zmcedw08pokz">
                    <div className="flex items-center justify-between mb-3" data-id="element-2669" data-mp="1zlumfbkm1wiv">
                      <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-2670" data-mp="1vmc32k8ftti5">
                        Recent Searches
                      </h3>
                      <button onClick={() => clearRecentSearches()} className="text-xs text-[#1E2B55] font-bold" data-id="element-2671" data-mp="2ncgl4c8q64g7">
                        Clear All
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-2672" data-mp="2edv4wnqq71il">
                      {recentSearches.map((search, index) => <button key={index} onClick={() => handleSearchSelect(search)} className="flex items-center px-4 py-2 rounded-full bg-gray-100 text-sm text-gray-700" data-id="element-2673" data-mp="12xeigagr7km8" data-mpi={index}>
                          <ClockIcon className="w-3.5 h-3.5 mr-2 text-gray-400" data-id="element-2674" data-mp="ssy3rkf0g9k0" />
                          {search}
                        </button>)}
                    </div>
                  </div>}
                {!searchQuery && <div data-id="element-2675" data-mp="12jjbpygkqqfl"><h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3" data-id="element-2676" data-mp="12kax6gggj7s5">Buy again</h3><div className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide" data-id="element-2677" data-mp="16jjqxlbhz5np">{nowQuickLists.map(list => <button key={list.name} type="button" onClick={() => {
                  navigate(list.path);
                  setShowSearchResults(false);
                  setSearchFocused(false);
                }} className="flex w-36 shrink-0 items-center gap-2 rounded-xl border border-gray-100 bg-[#f8fbfb] p-2 text-left" data-id="element-2678" data-mp="1biek9h73ccm2"><img src={list.image} alt="" className="h-10 w-10 rounded-lg object-cover" data-id="element-2679" data-mp="1lf9fnr82hww4" /><span className="min-w-0" data-id="element-2680" data-mp="3ibgnqmf3h5pb"><span className="block truncate text-xs font-bold text-gray-900" data-id="element-2681" data-mp="3ic8i5twygxe0">{list.name}</span><span className="mt-0.5 block text-[10px] text-gray-500" data-id="element-2682" data-mp="3n98p071mq466">Everyday essentials</span></span></button>)}</div></div>}
                {!searchQuery && <div data-id="element-2683" data-mp="1cm4w7bofzguq"><h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3" data-id="element-2684" data-mp="1cm4y5h805qxd">Shop a category</h3><div className="flex flex-wrap gap-2" data-id="element-2685" data-mp="1qlwevppwllf2">{nowQuickCategories.filter(category => ['medicines', 'vitamins-supplements', 'baby-mother-care'].includes(category.slug)).map(category => <button key={category.slug} type="button" onClick={() => handleNowCategorySearch(category.slug)} className="rounded-full bg-[#eef9f8] px-3 py-2 text-xs font-bold text-[#16726c]" data-id="element-2686" data-mp="xwp863gappiq">{category.name}</button>)}</div></div>}
                {!searchQuery && <div data-id="element-2687" data-mp="17ntnx5dspeem">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3" data-id="element-2688" data-mp="12vsedr80i1sa">
                      Popular Searches
                    </h3>
                    <div className="flex flex-wrap gap-2" data-id="element-2689" data-mp="f4mun0yyapcp">
                      {POPULAR_SEARCHES.map((search, index) => <button key={index} onClick={() => handleSearchSelect(search)} className="px-4 py-2 bg-blue-50 text-[#1E2B55] rounded-full text-xs font-bold" data-id="element-2690" data-mp="a5a9qlrn3scr" data-mpi={index}>
                          {search}
                        </button>)}
                    </div>
                  </div>}
                {!searchQuery && TRENDING_PRODUCTS.length > 0 && <div data-id="element-2691" data-mp="35gmm4c1an166">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3" data-id="element-2692" data-mp="39jssdjoeoscn">
                      Suggested Products
                    </h3>
                    <div className="space-y-3" data-id="element-2693" data-mp="2vmdtzpg6da8j">
                      {TRENDING_PRODUCTS.map(product => <button key={product.id} onClick={() => handleProductClick(product.id)} className="flex items-center w-full text-left p-3 bg-gray-50 rounded-2xl transition-colors" data-id="element-2694" data-mp="30l7bdsyl1ldz">
                          <div className="w-14 h-14 bg-white rounded-xl overflow-hidden flex-shrink-0 border border-gray-100" data-id="element-2695" data-mp="153xjo0mmcvov">
                            <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-2696" data-mp="w99esbkyhg0l" />
                          </div>
                          <div className="ml-3 flex-1 min-w-0" data-id="element-2697" data-mp="m9ihy38xaoxc">
                            <p className="text-sm font-bold text-gray-900 truncate" data-id="element-2698" data-mp="hbh4bxu9nht9">
                              {product.name}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5" data-id="element-2699" data-mp="8c3omnfjtx03">
                              {product.category}
                            </p>
                            <p className="text-sm font-black text-[#1E2B55] mt-1" data-id="element-2700" data-mp="27ofyvnh1l2ki">
                              {product.price.toFixed(2)} SAR
                            </p>
                          </div>
                          <ChevronRightIcon className="w-5 h-5 text-gray-400 ml-2 flex-shrink-0" data-id="element-2701" data-mp="2a946hcune9j0" />
                        </button>)}
                    </div>
                  </div>}
                {searchQuery && <div data-id="element-2702" data-mp="2oyjsygn5bssw">
                    <div className="flex items-center justify-between mb-4" data-id="element-2703" data-mp="2owz1x101irzs">
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-2704" data-mp="1rbbg1a17ns2i">
                        Results for "{searchQuery}"
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-2705" data-mp="218xvbxmf9ln0">
                        {TRENDING_PRODUCTS.filter(product => product.name.toLowerCase().includes(searchQuery.toLowerCase())).length}{' '}
                        items found
                      </p>
                    </div>
                    <div className="space-y-3" data-id="element-2706" data-mp="6m44u7z9nqbu">
                      {TRENDING_PRODUCTS.filter(product => product.name.toLowerCase().includes(searchQuery.toLowerCase())).slice(0, searchResultsLimit).map(product => <button key={product.id} type="button" onClick={() => handleProductClick(product.id)} className="flex w-full items-center rounded-2xl border border-gray-100 bg-white p-3 text-left shadow-[0_2px_10px_rgba(20,54,52,0.04)] transition-[border-color,background-color] duration-150 hover:border-[#a5d6d1]" data-id="element-2707" data-mp="pb27xvcjyyir">
                            <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl border border-gray-100 bg-white" data-id="element-2708" data-mp="ubyb26p2ygn4"><img src={product.image} alt={product.name} className="h-full w-full object-cover" data-id="element-2709" data-mp="311wjb41ljobu" /></div>
                            <div className="ml-3 min-w-0 flex-1" data-id="element-2710" data-mp="2w4wbanl6sbi3"><p className="truncate text-sm font-bold text-gray-900" data-id="element-2711" data-mp="1bjzdyciap7p7">{product.name}</p><div className="mt-1 flex flex-wrap gap-1.5" data-id="element-2712" data-mp="1gjlyz84d7s8k"><span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700" data-id="element-2713" data-mp="1gikeye5dfvep">{product.inStock ? 'Available now' : 'Check availability'}</span>{product.requiresPrescription && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700" data-id="element-2714" data-mp="16sr1f7lo567t">Prescription required</span>}<span className="rounded-full bg-[#eef9f8] px-2 py-0.5 text-[10px] font-bold text-[#16726c]" data-id="element-2715" data-mp="ssg9s2cjbnll">{product.deliveryEta}</span></div><p className="mt-1.5 text-sm font-black text-[#1E2B55]" data-id="element-2716" data-mp="wt9wise8qwcw">{product.price.toFixed(2)} SAR</p></div>
                            <ChevronRightIcon className="ml-2 h-5 w-5 shrink-0 text-gray-400" data-id="element-2717" data-mp="nuogp505lvft" />
                          </button>)}
                    </div>
                    {TRENDING_PRODUCTS.filter(product => product.name.toLowerCase().includes(searchQuery.toLowerCase())).length > searchResultsLimit && <div className="mt-6" data-id="element-2718" data-mp="3p0q924r8afou">
                        <button onClick={() => setSearchResultsLimit(searchResultsLimit + 5)} className="w-full py-3 bg-gray-100 text-gray-900 font-bold rounded-xl text-sm" data-id="element-2719" data-mp="3oxc54s2x1yfa">
                          Show More Results
                        </button>
                      </div>}
                    {TRENDING_PRODUCTS.filter(product => product.name.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 && <div className="py-6 px-2" data-id="element-2720" data-mp="2ict2ww7bw80z">
                        <div className="flex items-start gap-3 mb-4" data-id="element-2721" data-mp="2na304y0ylnzd">
                          <div className="w-12 h-12 rounded-full bg-[#EEF0F6] flex items-center justify-center flex-shrink-0" data-id="element-2722" data-mp="2ncfihcamq0cy">
                            <span className="text-2xl" data-id="element-2723" data-mp="2b0ddplsjo5oh">🧑‍⚕️</span>
                          </div>
                          <div data-id="element-2724" data-mp="260r03b8t0nf5">
                            <h3 className="text-base font-bold text-gray-900" data-id="element-2725" data-mp="2622153av7h2k">
                              No Matching Search Results
                            </h3>
                            <p className="text-sm text-gray-500 mt-1" data-id="element-2726" data-mp="2ypej1062z0pj">
                              The product doesn't seem to be available right
                              now. Don't worry — you can try other keywords, or
                              contact our pharmacist for help.
                            </p>
                          </div>
                        </div>
                        <ul className="text-sm text-gray-600 space-y-1.5 mb-5 pl-5 list-disc" data-id="element-2727" data-mp="1dcbn0u98gf6g">
                          <li data-id="element-2728" data-mp="18d8hl8pcc6xy">
                            Make sure the product name is spelled correctly.
                          </li>
                          <li data-id="element-2729" data-mp="eunyccgbpsu">Try a shorter or more general keyword.</li>
                        </ul>
                        <div className="border-t border-gray-200 pt-4 mb-5" data-id="element-2730" data-mp="4ed7p4sjduu3">
                          <h4 className="text-sm font-bold text-gray-900 mb-3" data-id="element-2731" data-mp="3mlj5jhb1ie2x">
                            Contact Customer Service / Ask a Pharmacist
                          </h4>
                          <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-3" data-id="element-2732" data-mp="3cv835z708cy5">
                            <div className="w-9 h-9 rounded-full bg-[#EEF0F6] flex items-center justify-center flex-shrink-0" data-id="element-2733" data-mp="3hlwrj5ne0tt2">
                              <span className="text-lg" data-id="element-2734" data-mp="33vb6pbedi9sj">💬</span>
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-2735" data-mp="38t36erl9f07a">
                              <p className="text-sm font-semibold text-gray-900" data-id="element-2736" data-mp="38r8mnglq5e4s">
                                Need quick help?
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-2737" data-mp="2yu41nk3m7opk">
                                Chat with an Innova pharmacist or leave us your
                                question.
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2 mt-3" data-id="element-2738" data-mp="2p8uk31g8v1xc">
                            <a href="https://wa.me/966500000000" target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-full border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-2739" data-mp="2k8q7vnmdctcx">
                              WhatsApp
                            </a>
                            <a href="tel:+966500000000" className="flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-full border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-2740" data-mp="3hw8nnieg6vqf">
                              Direct Call
                            </a>
                          </div>
                        </div>
                      </div>}
                  </div>}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Legacy sidebar disabled: mobile navigation now opens the /menu page. */}
      <AnimatePresence data-id="element-2741" data-mp="wa5ueihntv8z">
        {false && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black bg-opacity-50 z-[60]" onClick={() => setShowCategoriesMenu(false)} data-id="element-2742" data-mp="wcsbp9ucrp4e" />
            <motion.div initial={{
          x: '-100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '-100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed inset-0 w-full min-h-[100dvh] bg-white z-[70] shadow-xl overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]" data-id="element-2743" data-mp="2hmqvbv3l08lq">
              <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-gray-200 bg-white" data-id="element-2744" data-mp="3egrv0sfc5mzg">
                <img src="https://cdn.magicpatterns.com/uploads/enT9iVMZr5bUJi1w413sX2/Layer_1_(1).png" alt="Dawaak" className="h-10 w-auto" data-id="element-2745" data-mp="39gvpgoqzpnhs" />
                <button onClick={() => setShowCategoriesMenu(false)} className="p-2 rounded-full hover:bg-gray-100" data-id="element-2746" data-mp="17p98lg0tb8z1">
                  <XIcon className="w-5 h-5 text-gray-500" data-id="element-2747" data-mp="2x7pkyt886tkx" />
                </button>
              </div>
              <div className="pb-[max(8rem,env(safe-area-inset-bottom))]" data-id="element-2748" data-mp="31yo3lzgos3rc">
                <div className="p-4 bg-gray-50 border-b border-gray-200" data-id="element-2749" data-mp="31zz49qeu4ve8">
                  <div className="flex items-center space-x-3" data-id="element-2750" data-mp="2nb30i5jta4jf">
                    <div className="w-10 h-10 rounded-full bg-[#1E2B55] flex items-center justify-center text-white" data-id="element-2751" data-mp="2s9ebwkpr8n3r">
                      <UserIcon className="w-5 h-5" data-id="element-2752" data-mp="3fygqtkt081uk" />
                    </div>
                    <div data-id="element-2753" data-mp="3kvz2hiyeov69">
                      <p className="text-sm font-medium" data-id="element-2754" data-mp="3kwic8cdwkd97">Hello, Guest</p>
                      <div className="flex space-x-2 mt-1" data-id="element-2755" data-mp="35ypvxi0j7kty">
                        <button onClick={openAuthModal} className="text-xs font-medium text-[#1E2B55] hover:text-[#162041]" data-id="element-2756" data-mp="3ayuc16xj2djn">
                          Sign In
                        </button>
                        <span className="text-xs text-gray-400" data-id="element-2757" data-mp="1zkz0e85u0mx6">|</span>
                        <button onClick={openAuthModal} className="text-xs font-medium text-[#1E2B55] hover:text-[#162041]" data-id="element-2758" data-mp="hkzozp5n1mkq">
                          Register
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-4 border-b border-gray-200" data-id="element-2759" data-mp="3v0p75wl72de6">
                  <button onClick={() => setLanguageBottomSheetOpen(true)} className="flex items-center justify-between w-full" data-id="element-2760" data-mp="3v174yxni8zo7">
                    <div className="flex items-center space-x-2" data-id="element-2761" data-mp="7pfvtaigl1zo">
                      <GlobeIcon className="w-4 h-4 text-gray-500" data-id="element-2762" data-mp="2wwd5z4wzgo0" />
                      <span className="text-sm" data-id="element-2763" data-mp="3bcy9admr6bjy">Language</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-700 bg-gray-100 px-2 py-1 rounded" data-id="element-2764" data-mp="3baljjear6wwc">
                      {language === 'en' ? 'English' : 'Arabic'}
                      <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-2765" data-mp="2lwy4gyvp3w" />
                    </div>
                  </button>
                </div>
                <div className="p-4 border-b border-gray-200" data-id="element-2766" data-mp="91pgtanyk8jg">
                  <LoyaltyCard progress={loyaltyProgress} onJoinNow={() => {
                setShowCategoriesMenu(false);
                setLoyaltyBottomSheetOpen(true);
              }} loyaltyData={loyaltyData} data-id="element-2767" data-mp="904n1n9gwtmn" />
                </div>
                <div className="p-4 border-b border-gray-200" data-id="element-2768" data-mp="spf537worqnx">
                  <h3 className="text-xs font-bold text-gray-500 mb-3" data-id="element-2769" data-mp="sq8ao5jwsa4h">MENU</h3>
                  <ul className="space-y-1" data-id="element-2770" data-mp="wpqm8apub8un">
                    <li className="py-1" data-id="element-2771" data-mp="11q4v1uz9mmtq">
                      <button onClick={() => {
                    setShowCategoriesMenu(false);
                    navigate('/categories');
                  }} className="group w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-bold px-4 py-3 rounded-lg shadow-md text-center flex items-center justify-center gap-2" data-id="element-2772" data-mp="11r65bx8gj5ae">
                        All Categories
                      </button>
                    </li>
                    <li data-id="element-2773" data-mp="12v6k1nso1rci">
                      <button onClick={() => handleCategoryClick('best-sellers')} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-2774" data-mp="1zjmmgkgvjv71">
                        <TrendingUpIcon className="w-4 h-4 mr-3" data-id="element-2775" data-mp="1psi73jnae8bo" />
                        <span className="text-sm" data-id="element-2776" data-mp="1lsr9ox398ty6">Best Sellers</span>
                        <span className="ml-auto text-xs bg-amber-500 text-white px-1.5 py-0.5 rounded-full" data-id="element-2777" data-mp="1lqeugrovsxot">
                          HOT
                        </span>
                      </button>
                    </li>
                    <li data-id="element-2778" data-mp="crrlmq99dmny">
                      <button onClick={() => handleCategoryClick('new-arrivals')} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-2779" data-mp="cqzohb00jgvk">
                        <ShoppingBagIcon className="w-4 h-4 mr-3" data-id="element-2780" data-mp="2zvd0l9jqe5h" />
                        <span className="text-sm" data-id="element-2781" data-mp="3v5ioto5swmvi">New Arrivals</span>
                        <span className="ml-auto text-xs bg-[#1E2B55] text-white px-1.5 py-0.5 rounded-full" data-id="element-2782" data-mp="3q77ezdfiacda">
                          NEW
                        </span>
                      </button>
                    </li>
                    <li data-id="element-2783" data-mp="3g99tgxp16tod">
                      <button onClick={() => {
                    setShowCategoriesMenu(false);
                    navigate('/offers');
                  }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-2784" data-mp="3gaudvuhmsw8i">
                        <TagIcon className="w-4 h-4 mr-3" data-id="element-2785" data-mp="2se7ucei6r0m5" />
                        <span className="text-sm" data-id="element-2786" data-mp="2sbllmbjk6f2c">Deals & Offers</span>
                        <span className="ml-auto text-xs bg-red-500 text-white px-1.5 py-0.5 rounded-full" data-id="element-2787" data-mp="2xay9mzm61s6i">
                          SALE
                        </span>
                      </button>
                    </li>
                  </ul>
                </div>
                <div className="p-4 border-b border-gray-200" data-id="element-2788" data-mp="29fc0j4a8oo8u">
                  <h3 className="text-xs font-bold text-gray-500 mb-3" data-id="element-2789" data-mp="29gwryl19br2h">
                    SHOP BY CATEGORY
                  </h3>
                  <ul className="space-y-1" data-id="element-2790" data-mp="2iez2psasly2y">
                    <li data-id="element-2791" data-mp="2idxhww3z5x81">
                      <button onClick={() => handleCategoryClick('medicines')} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-2792" data-mp="2nebpye5kt762">
                        <span className="text-sm" data-id="element-2793" data-mp="2mt0ybv1wj0m">Medicines (OTC)</span>
                        <ChevronRightIcon className="w-4 h-4 ml-auto" data-id="element-2794" data-mp="1yqpmsz00uqzc" />
                      </button>
                    </li>
                    <li data-id="element-2795" data-mp="1uovsr9kc0l85">
                      <button onClick={() => handleCategoryClick('vitamins-supplements')} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-2796" data-mp="1urqc0xrlzzzz">
                        <span className="text-sm" data-id="element-2797" data-mp="1fyeq5mvynp9o">Vitamins & Supplements</span>
                        <ChevronRightIcon className="w-4 h-4 ml-auto" data-id="element-2798" data-mp="1b3hles5ps22l" />
                      </button>
                    </li>
                    <li data-id="element-2799" data-mp="1psdn87h6giur">
                      <button onClick={() => handleCategoryClick('personal-care')} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-2800" data-mp="1kzc6rv1bob92">
                        <span className="text-sm" data-id="element-2801" data-mp="sawx7o0yml54">Personal Care</span>
                        <ChevronRightIcon className="w-4 h-4 ml-auto" data-id="element-2802" data-mp="33xqll01cgmlf" />
                      </button>
                    </li>
                    <li data-id="element-2803" data-mp="38qjrhr8ju0z3">
                      <button onClick={() => handleCategoryClick('mother-baby')} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-2804" data-mp="2uxu53lp3yu1g">
                        <span className="text-sm" data-id="element-2805" data-mp="3mqb9s463m3iw">Mother & Baby</span>
                        <ChevronRightIcon className="w-4 h-4 ml-auto" data-id="element-2806" data-mp="3romksi84qk2p" />
                      </button>
                    </li>
                  </ul>
                </div>
                <div className="p-4 border-b border-gray-200" data-id="element-2807" data-mp="3dqo6fctt6nn7">
                  <h3 className="text-xs font-bold text-gray-500 mb-3" data-id="element-2808" data-mp="3ilv689jyj86u">
                    BROWSE BY CONDITION
                  </h3>
                  <div className="grid grid-cols-2 gap-3" data-id="element-2809" data-mp="22bj3c7prmn32">
                    {conditions.map((condition, index) => <button key={index} onClick={() => handleConditionClick(condition.slug)} className="inline-flex items-center gap-1.5 border border-gray-200 bg-gray-50 text-sm text-gray-800 px-3 py-2 rounded-full whitespace-nowrap" data-id="element-2810" data-mp="2mifys1szivq3" data-mpi={index}>
                        <span className="flex-shrink-0" data-id="element-2811" data-mp="28rsxq2zr4tqu">{condition.icon}</span>
                        <span className="font-medium text-xs" data-id="element-2812" data-mp="35h0opqj53v25">
                          {condition.name}
                        </span>
                      </button>)}
                  </div>
                </div>
                <div className="p-4 border-b border-gray-200" data-id="element-2813" data-mp="2wej23azkp0em">
                  <h3 className="text-xs font-bold text-gray-500 mb-3" data-id="element-2814" data-mp="2rhsiwesh1ooz">
                    MY ACCOUNT
                  </h3>
                  <ul className="space-y-1" data-id="element-2815" data-mp="1l2i6pd87edpw">
                    <li data-id="element-2816" data-mp="1g1u8o7lk4wm6">
                      <button onClick={() => {
                    setShowCategoriesMenu(false);
                    navigate('/account');
                  }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-2817" data-mp="1g1koj0n71joe">
                        <UserIcon className="w-4 h-4 mr-3" data-id="element-2818" data-mp="4idfvnmsv0pl" />
                        <span className="text-sm" data-id="element-2819" data-mp="9c87dd8ncjz8">My Account</span>
                      </button>
                    </li>
                    <li data-id="element-2820" data-mp="e9rkg9tlyqpl">
                      <button onClick={() => {
                    setShowCategoriesMenu(false);
                    navigate('/account/orders');
                  }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-2821" data-mp="ecnj60da5jf3">
                        <PackageIcon className="w-4 h-4 mr-3" data-id="element-2822" data-mp="2q0vjgzg85bl7" />
                        <span className="text-sm" data-id="element-2823" data-mp="2u15pgc40o202">My Orders</span>
                      </button>
                    </li>
                    <li data-id="element-2824" data-mp="165i5thyl0pv">
                      <button onClick={() => {
                    setShowCategoriesMenu(false);
                    navigate('/account/wishlist');
                  }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-2825" data-mp="17qftrzkkzry">
                        <HeartIcon className="w-4 h-4 mr-3" data-id="element-2826" data-mp="k5jg0mit6vln" />
                        <span className="text-sm" data-id="element-2827" data-mp="k3ynt3jyvoqy">My Wishlist</span>
                        {currentWishlistCount > 0 && <span className="ml-auto bg-[#1E2B55] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center" data-id="element-2828" data-mp="tvmiddbtllwn">
                            {currentWishlistCount}
                          </span>}
                      </button>
                    </li>
                  </ul>
                </div>
                <div className="p-4 border-t border-gray-200 pb-32" data-id="element-2829" data-mp="xyixuv62d1wd">
                  <button onClick={() => {
                setShowCategoriesMenu(false);
              }} className="flex items-center text-red-500 font-medium hover:text-red-600 w-full" data-id="element-2830" data-mp="1hihnkevj3urn">
                    <LogOutIcon className="w-4 h-4 mr-2" data-id="element-2831" data-mp="18ke7vz785s7r" />
                    <span className="text-sm" data-id="element-2832" data-mp="1civ9k37vmahs">Sign Out</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      <LanguageBottomSheet isOpen={languageBottomSheetOpen} onClose={() => setLanguageBottomSheetOpen(false)} currentLanguage={language} onLanguageChange={handleLanguageChange} data-id="element-2833" data-mp="13k9w2mh9hlnv" />

      <AuthBottomSheet isOpen={authBottomSheetOpen} onClose={() => setAuthBottomSheetOpen(false)} onSuccess={handleAuthSuccess} data-id="element-2834" data-mp="ttyg21kazm0m" />

      <LoyaltyOnboardingBottomSheet isOpen={loyaltyBottomSheetOpen} onClose={() => setLoyaltyBottomSheetOpen(false)} onComplete={handleLoyaltyComplete} data-id="element-2835" data-mp="kt8o58y5yfs6" />
    </>;
}